import { AxiosError } from 'axios';
import { put, call, select, takeLatest } from 'redux-saga/effects';
import { SagaIterator } from 'redux-saga';
import { selectors as clientSelectors } from 'store/clients';
import { API } from 'services/api/base';
import { getErrorCode } from 'services/api/normalizer';
import {
  types,
  GetDeliveryLogRequest,
  GetDeliveryLogResponse,
  GetTransactionPriceTypesRequest,
  TransactionPriceType,
  ReconcileTransactionsRequest,
  GetSettlementReportResponse,
  GetSettlementReportRequest,
  GetSalesReportResponse,
  GetSalesReportRequest,
  GetSalesByCardTypeReportRequest,
  GetSalesByCardTypeReportResponse
} from './types';

export function* getDeliveryLogAsync({ payload }: GetDeliveryLogRequest) {
  const { page, startTime, endTime, exportData = false, clientIntegrationId, ...otherParams } = payload;

  yield put({ type: types.GET_DELIVERY_LOG.PENDING, payload: { pending: true, exportData } });

  try {
    const clientId: string = yield select(clientSelectors.selectClientId);

    let currentDate;
    let lastDate;

    if (!startTime) {
      currentDate = new Date();
      currentDate.setDate(currentDate.getDate() - 90);
      currentDate = currentDate.toISOString();
    }

    if (!endTime) {
      lastDate = new Date().toISOString();
    }

    const api = new API(`clients/${clientId}/clientIntegrations/${clientIntegrationId}/transactions/search`);

    const response: GetDeliveryLogResponse = yield call(api.create, {
      page,
      limit: exportData ? null : 100,
      sort: otherParams.sort,
      sortColumn: otherParams.sortColumn,
      searchValue: otherParams.searchValue,
      startTime: startTime || currentDate,
      endTime: endTime || lastDate,
      kioskIds: otherParams.kioskIds,
      flows: otherParams.flows,
      deliveryTypes: otherParams.deliveryTypes
    });

    if (!exportData && (page === 1 || page === undefined)) {
      yield put({ type: types.RESET_DELIVERY_LOG });
    }

    yield put({
      type: types.GET_DELIVERY_LOG.SUCCESS,
      payload: { ...response, exportData }
    });
  } catch (err) {
    const errorCode = getErrorCode(err as AxiosError) as number;

    if (errorCode >= 400 && errorCode < 500) {
      yield put({ type: types.GET_DELIVERY_LOG.ERROR, payload: 'Invalid credentials' });
    } else if (errorCode >= 500) {
      yield put({ type: types.GET_DELIVERY_LOG.ERROR, payload: 'Server error' });
    } else {
      yield put({ type: types.GET_DELIVERY_LOG.ERROR, payload: 'Uh oh! Something went wrong. Please try again.' });
    }
  } finally {
    yield put({ type: types.GET_DELIVERY_LOG.PENDING, payload: { pending: false, exportData } });
  }
}

export function* getTransactionPriceTypesAsync({ payload }: GetTransactionPriceTypesRequest) {
  const { transactionId, clientIntegrationId } = payload;

  yield put({
    type: types.GET_TRANSACTION_PRICE_TYPES.PENDING,
    payload: true
  });

  try {
    const clientId: string = yield select(clientSelectors.selectClientId);

    const api = new API(
      `clients/${clientId}/clientIntegrations/${clientIntegrationId}/transactions/${transactionId}/priceTypes`
    );

    const response: TransactionPriceType[] = yield call(api.getAll);

    yield put({
      type: types.GET_TRANSACTION_PRICE_TYPES.SUCCESS,
      payload: response
    });
  } catch (err) {
    yield put({
      type: types.GET_TRANSACTION_PRICE_TYPES.SUCCESS,
      payload: []
    });
  } finally {
    yield put({
      type: types.GET_TRANSACTION_PRICE_TYPES.PENDING,
      payload: false
    });
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function* refundTransactionAsync({ payload }: any): SagaIterator {
  const { transactionId, clientIntegrationId, email } = payload;

  try {
    const clientId: string = yield select(clientSelectors.selectClientId);

    const api = new API(
      `clients/${clientId}/clientIntegrations/${clientIntegrationId}/transactions/${transactionId}/refund`
    );

    const response = yield call(api.create, {
      email,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    });

    yield put({
      type: types.REFUND_TRANSACTION.SUCCESS,
      payload: response
    });
  } catch (err) {
    yield put({
      type: types.REFUND_TRANSACTION.ERROR,
      payload: 'Refund failed'
    });
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function* voidTransactionAsync({ payload }: any): SagaIterator {
  const { transactionId, clientIntegrationId } = payload;

  try {
    const clientId: string = yield select(clientSelectors.selectClientId);

    const api = new API(
      `clients/${clientId}/clientIntegrations/${clientIntegrationId}/transactions/${transactionId}/void`
    );

    const response = yield call(api.create, {});

    yield put({
      type: types.VOID_TRANSACTION.SUCCESS,
      payload: response
    });
  } catch (err) {
    yield put({
      type: types.VOID_TRANSACTION.ERROR,
      payload: 'Void failed'
    });
  }
}

export function* reconcileTransactionsAsync({ payload }: ReconcileTransactionsRequest) {
  const { clientIntegrationId, startTime, endTime, identifyOnly } = payload;

  try {
    const clientId: string = yield select(clientSelectors.selectClientId);

    const api = new API(`clients/${clientId}/clientIntegrations/${clientIntegrationId}/transactions/reconcile`);

    yield call(api.create, {
      startTime,
      endTime,
      identifyOnly
    });

    yield put({
      type: types.RECONCILE_TRANSACTIONS.SUCCESS
    });
  } catch (err) {
    yield put({
      type: types.RECONCILE_TRANSACTIONS.ERROR,
      payload: 'Reconciliation failed'
    });
  }
}

export function* getSettlementReportAsync({ payload }: GetSettlementReportRequest) {
  const { clientIntegrationId, startTime, endTime } = payload;

  yield put({
    type: types.GET_SETTLEMENT_REPORT.PENDING,
    payload: true
  });

  try {
    const clientId: string = yield select(clientSelectors.selectClientId);

    const api = new API(`clients/${clientId}/clientIntegrations/${clientIntegrationId}/reportTransactions/settlement`);

    const response: GetSettlementReportResponse = yield call(api.create, {
      startTime,
      endTime
    });

    yield put({
      type: types.GET_SETTLEMENT_REPORT.SUCCESS,
      payload: response
    });
  } catch (err) {
    const errorCode = getErrorCode(err as AxiosError) as number;

    if (errorCode >= 400 && errorCode < 500) {
      yield put({
        type: types.GET_SETTLEMENT_REPORT.ERROR,
        payload: 'Invalid request'
      });
    } else if (errorCode >= 500) {
      yield put({
        type: types.GET_SETTLEMENT_REPORT.ERROR,
        payload: 'Server error'
      });
    } else {
      yield put({
        type: types.GET_SETTLEMENT_REPORT.ERROR,
        payload: 'Something went wrong'
      });
    }
  } finally {
    yield put({
      type: types.GET_SETTLEMENT_REPORT.PENDING,
      payload: false
    });
  }
}

export function* getSalesReportAsync({ payload }: GetSalesReportRequest) {
  const { clientIntegrationId, startTime, endTime } = payload;

  yield put({
    type: types.GET_SALES_REPORT.PENDING,
    payload: true
  });

  try {
    const clientId: string = yield select(clientSelectors.selectClientId);

    const api = new API(`clients/${clientId}/clientIntegrations/${clientIntegrationId}/reportTransactions/sales`);

    const response: GetSalesReportResponse = yield call(api.create, {
      startTime,
      endTime
    });

    yield put({
      type: types.GET_SALES_REPORT.SUCCESS,
      payload: response
    });
  } catch (err) {
    const errorCode = getErrorCode(err as AxiosError) as number;

    if (errorCode >= 400 && errorCode < 500) {
      yield put({
        type: types.GET_SALES_REPORT.ERROR,
        payload: 'Invalid request'
      });
    } else if (errorCode >= 500) {
      yield put({
        type: types.GET_SALES_REPORT.ERROR,
        payload: 'Server error'
      });
    } else {
      yield put({
        type: types.GET_SALES_REPORT.ERROR,
        payload: 'Something went wrong'
      });
    }
  } finally {
    yield put({
      type: types.GET_SALES_REPORT.PENDING,
      payload: false
    });
  }
}

export function* getSalesByCardTypeReportAsync({ payload }: GetSalesByCardTypeReportRequest) {
  const { clientIntegrationId, startTime, endTime } = payload;

  yield put({
    type: types.GET_SALES_BY_CARD_TYPE_REPORT.PENDING,
    payload: true
  });

  try {
    const clientId: string = yield select(clientSelectors.selectClientId);

    const api = new API(
      `clients/${clientId}/clientIntegrations/${clientIntegrationId}/reportTransactions/salesByCardType`
    );

    const response: GetSalesByCardTypeReportResponse = yield call(api.create, {
      startTime,
      endTime
    });

    yield put({
      type: types.GET_SALES_BY_CARD_TYPE_REPORT.SUCCESS,
      payload: response
    });
  } catch (err) {
    const errorCode = getErrorCode(err as AxiosError) as number;

    if (errorCode >= 400 && errorCode < 500) {
      yield put({
        type: types.GET_SALES_BY_CARD_TYPE_REPORT.ERROR,
        payload: 'Invalid request'
      });
    } else if (errorCode >= 500) {
      yield put({
        type: types.GET_SALES_BY_CARD_TYPE_REPORT.ERROR,
        payload: 'Server error'
      });
    } else {
      yield put({
        type: types.GET_SALES_BY_CARD_TYPE_REPORT.ERROR,
        payload: 'Something went wrong'
      });
    }
  } finally {
    yield put({
      type: types.GET_SALES_BY_CARD_TYPE_REPORT.PENDING,
      payload: false
    });
  }
}

export const sagas = [
  takeLatest(types.GET_DELIVERY_LOG.REQUEST, getDeliveryLogAsync),
  takeLatest(types.GET_TRANSACTION_PRICE_TYPES.REQUEST, getTransactionPriceTypesAsync),
  takeLatest(types.REFUND_TRANSACTION.REQUEST, refundTransactionAsync),
  takeLatest(types.VOID_TRANSACTION.REQUEST, voidTransactionAsync),
  takeLatest(types.RECONCILE_TRANSACTIONS.REQUEST, reconcileTransactionsAsync),
  takeLatest(types.GET_SETTLEMENT_REPORT.REQUEST, getSettlementReportAsync),
  takeLatest(types.GET_SALES_REPORT.REQUEST, getSalesReportAsync),
  takeLatest(types.GET_SALES_BY_CARD_TYPE_REPORT.REQUEST, getSalesByCardTypeReportAsync)
];
